"use client";
import React, { useEffect, useState, useCallback } from "react";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  DollarSign,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import EditBusinessModal from "./EditBusinessModal";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const buildHeaders = (token) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function YourBusinessMyBusiness() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  const fetchUserInfo = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/auth/userinfo/`, {
        headers,
      });

      if (res.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBusiness(data);
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError("Failed to load user info.");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/service/currencies/`, {
          headers,
        });
        const data = await res.json();
        if (Array.isArray(data)) setCurrencies(data);
      } catch (err) {
        console.warn("⚠️ Currency fetch failed:", err);
      }
    };
    fetchCurrencies();
  }, [token]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-[320px] bg-white rounded-2xl shadow text-gray-400">
        Loading user info...
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-[320px] bg-white rounded-2xl shadow text-red-500">
        {error}
      </div>
    );

  const user = business?.user || {};
  const image = user.image_url || "";
  const userType = user.status;
  const currentCurrency = currencies.find(
    (c) => c.short_name === business?.currency
  );

  const displayName = `${user.first_name} ${user.last_name}`;
  const isSalonView = userType === "salon" || userType === "individual";

  return (
    <>
      <Toaster position="bottom-right" reverseOrder={false} />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full bg-white rounded-2xl p-8 shadow-sm border border-gray-100 transition-all"
        style={{marginTop:"0px"}}
      >
        {userType === "salon" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-4"
          >
            <Building2 className="w-7 h-7 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              My Business
            </h2>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex justify-between items-center mb-6"
        >
          <motion.h3
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[22px] font-bold text-gray-900"
          >
            {displayName}
          </motion.h3>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700 text-sm font-medium hover:border-black hover:text-black transition-all cursor-pointer"
          >
            Edit
            <Pencil className="w-4 h-4" />
          </motion.button>
        </motion.div>

        {userType === "salon" && (
          <motion.p
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-[16px] text-gray-500 mt-[-10px] mb-6"
          >
            {user.first_name} {user.last_name}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-8"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="w-[160px] h-[160px] rounded-full overflow-hidden shadow-inner flex-shrink-0"
          >
            <img src={image} alt="Business" className="w-full h-full object-cover" />
          </motion.div>

          <div className="flex flex-col justify-between w-full">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="grid grid-cols-2 gap-y-6 gap-x-10"
            >
              {isSalonView && (
                <>
                  <InfoItem
                    icon={<Phone className="w-5 h-5 text-black" />}
                    title="Phone"
                    value={
                      business?.phone_number ? (
                        <a
                          href={`tel:${business.phone_number}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {business.phone_number}
                        </a>
                      ) : (
                        "No phone"
                      )
                    }
                  />
                  <InfoItem
                    icon={<Mail className="w-5 h-5 text-black" />}
                    title="Email"
                    value={
                      user.email ? (
                        <a
                          href={`mailto:${user.email}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {user.email}
                        </a>
                      ) : (
                        "No email"
                      )
                    }
                  />
                  <InfoItem
                    icon={<MapPin className="w-5 h-5 text-black" />}
                    title="Location"
                    value={
                      <a
                        href={business?.address_url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-black transition-colors cursor-pointer"
                      >
                        {business?.address || "Unknown address"}
                      </a>
                    }
                  />
                  <InfoItem
                    icon={
                      currentCurrency ? (
                        <img
                          src={currentCurrency.icon}
                          alt={currentCurrency.short_name}
                          className="w-6 h-6 rounded-full border border-gray-200 object-cover"
                        />
                      ) : (
                        <DollarSign className="w-5 h-5 text-black" />
                      )
                    }
                    title="Currency"
                    value={
                      currentCurrency
                        ? currentCurrency.short_name
                        : business?.currency || "USD"
                    }
                  />
                </>
              )}

              {userType === "worker" && (
                <>
                  <InfoItem
                    icon={<Phone className="w-5 h-5 text-black" />}
                    title="Phone"
                    value={
                      business?.phone_number ? (
                        <a
                          href={`tel:${business.phone_number}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {business.phone_number}
                        </a>
                      ) : (
                        "No phone"
                      )
                    }
                  />
                  <InfoItem
                    icon={<Mail className="w-5 h-5 text-black" />}
                    title="Email"
                    value={
                      user.email ? (
                        <a
                          href={`mailto:${user.email}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {user.email}
                        </a>
                      ) : (
                        "No email"
                      )
                    }
                  />
                </>
              )}
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {showModal && (
        <EditBusinessModal
          onClose={(updated) => {
            setShowModal(false);
            if (updated) fetchUserInfo();
          }}
          userData={business}
          userType={userType === "individual" ? "salon" : userType}
        />
      )}
    </>
  );
}

function InfoItem({ icon, title, value }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-3 group transition-all cursor-pointer"
    >
      <div className="bg-gray-100 group-hover:bg-gray-200 w-10 h-10 rounded-lg flex justify-center items-center mt-[2px] flex-shrink-0 transition-all">
        {icon}
      </div>
      <div className="flex flex-col max-w-[320px] break-words">
        <p className="text-sm font-bold text-gray-800 leading-snug mb-1">
          {title}
        </p>
        <p className="text-[15px] text-gray-600 leading-snug break-words">
          {value}
        </p>
      </div>
    </motion.div>
  );
}